import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { navItems } from '../mock/mockData';

const sidebarWidthClass = (isMobile, open) => {
  if (isMobile) return open ? 'w-64 translate-x-0' : 'w-64 -translate-x-full';
  return open ? 'w-64' : 'w-20';
};
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { toast } from 'sonner';

const Sidebar = ({ open, setOpen, isMobile }) => {
  const { user, hasPermission } = useAuth();

  const visibleItems = navItems.filter(item =>
    !item.module || hasPermission(item.module, 'view')
  );

  return (
    <>
      {/* Backdrop overlay on mobile */}
      {isMobile && open && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      <aside
        className={`sidebar-bg text-slate-200 fixed lg:sticky lg:top-0 z-40 h-screen transition-all duration-300
          ${sidebarWidthClass(isMobile, open)}
          overflow-hidden`}
      >
        <div className="flex items-center gap-2 px-5 h-16 border-b border-white/10">
          <div className="w-9 h-9 rounded-md bg-brand-gold flex items-center justify-center text-[#0F1F38] font-display font-bold shrink-0">
            K
          </div>
          {(open || isMobile) && (
            <div className="leading-tight">
              <div className="font-display font-bold text-white text-base tracking-wide">KIANA</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-brand-gold">Developers CRM</div>
            </div>
          )}
        </div>

        <nav className="px-3 py-4 space-y-1 overflow-y-auto max-h-[calc(100vh-5rem)]">
          {visibleItems.map((item) => {
            const Icon = Icons[item.icon] || Icons.Circle;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => isMobile && setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors
                  ${isActive
                    ? 'bg-white/10 text-white border-l-2 border-brand-gold'
                    : 'text-slate-300 hover:bg-white/5 hover:text-white'}`
                }
              >
                <Icon className="w-[18px] h-[18px] shrink-0" />
                {(open || isMobile) && <span className="truncate">{item.label}</span>}
              </NavLink>
            );
          })}
        </nav>

        {!isMobile && (
          <button
            onClick={() => setOpen(!open)}
            className="hidden lg:flex absolute -right-3 top-20 w-6 h-6 bg-white border shadow rounded-full items-center justify-center text-slate-700 hover:bg-slate-50"
          >
            {open ? <Icons.ChevronLeft className="w-3.5 h-3.5" /> : <Icons.ChevronRight className="w-3.5 h-3.5" />}
          </button>
        )}
      </aside>
    </>
  );
};

const Topbar = ({ onMenu }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const title = navItems.find(n => location.pathname.startsWith(n.path))?.label || 'Dashboard';

  return (
    <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-3 sm:px-4 lg:px-6 sticky top-0 z-30">
      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
        <button onClick={onMenu} className="lg:hidden p-2 rounded hover:bg-slate-100">
          <Icons.Menu className="w-5 h-5 text-slate-700" />
        </button>
        <div className="min-w-0">
          <div className="text-[10px] sm:text-[11px] text-slate-500 uppercase tracking-wider">Kiana CRM</div>
          <div className="font-display font-bold text-base sm:text-lg text-slate-900 -mt-0.5 truncate">{title}</div>
        </div>
      </div>

      <div className="hidden md:flex items-center flex-1 max-w-md mx-8">
        <div className="relative w-full">
          <Icons.Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search leads, contacts, projects..." className="pl-9 bg-slate-50 border-slate-200" />
        </div>
      </div>

      <div className="flex items-center gap-1 sm:gap-2">
        <Button
          size="sm"
          className="bg-brand-navy hover:bg-[#13294A] text-white hidden md:flex"
          onClick={() => toast.success('Auto-dialer started — connecting to next 25 leads')}
        >
          <Icons.Phone className="w-4 h-4 mr-2" /> Auto Dialer
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="relative"
          onClick={() => toast('You have 5 new notifications')}
        >
          <Icons.Bell className="w-5 h-5 text-slate-600" />
          <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-brand-gold" />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 pl-1 pr-2 sm:pl-2 sm:pr-3 py-1.5 rounded-full hover:bg-slate-100" data-testid="user-menu-trigger">
              <Avatar className="w-8 h-8">
                <AvatarImage src={user?.avatar} />
                <AvatarFallback>{(user?.name || 'A').slice(0, 1)}</AvatarFallback>
              </Avatar>
              <div className="hidden md:block text-left leading-tight">
                <div className="text-sm font-semibold text-slate-800">{user?.name}</div>
                <div className="text-[10px] text-slate-500">{user?.role}</div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="break-all">{user?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem><Icons.User className="w-4 h-4 mr-2" /> Profile</DropdownMenuItem>
            <DropdownMenuItem><Icons.Settings className="w-4 h-4 mr-2" /> Settings</DropdownMenuItem>
            <DropdownMenuItem><Icons.HelpCircle className="w-4 h-4 mr-2" /> Help & Support</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-red-600 focus:text-red-700"
              onClick={() => { logout(); navigate('/login'); }}
              data-testid="logout-btn"
            >
              <Icons.LogOut className="w-4 h-4 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

const AppLayout = () => {
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 1024);
  const [open, setOpen] = useState(typeof window !== 'undefined' && window.innerWidth >= 1024);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      setOpen(!mobile);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar open={open} setOpen={setOpen} isMobile={isMobile} />
      <div className="flex-1 min-w-0 flex flex-col">
        <Topbar onMenu={() => setOpen(o => !o)} />
        <main className="flex-1 p-3 sm:p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AppLayout;